import { Route } from '@angular/router';
import { authGuard } from '@system-dyg/core/auth';
import { EXECUTIVE_VIEWS } from './pages/welcome/executive/welcome-executive-views';

const WELCOME_SECTION_LOADERS = {
  executive: () =>
    import('./pages/welcome/executive/welcome-executive.component').then(
      (m) => m.WelcomeExecutiveComponent,
    ),
  accounting: () =>
    import('./pages/welcome/accounting/welcome-accounting.component').then(
      (m) => m.WelcomeAccountingComponent,
    ),
  taxes: () =>
    import('./pages/welcome/taxes/welcome-taxes.component').then(
      (m) => m.WelcomeTaxesComponent,
    ),
  operations: () =>
    import('./pages/welcome/operations/welcome-operations.component').then(
      (m) => m.WelcomeOperationsComponent,
    ),
  inventory: () =>
    import('./pages/welcome/inventory/welcome-inventory.component').then(
      (m) => m.WelcomeInventoryComponent,
    ),
  warehouses: () =>
    import('./pages/welcome/warehouses/welcome-warehouses.component').then(
      (m) => m.WelcomeWarehousesComponent,
    ),
  marketing: () =>
    import('./pages/welcome/marketing/welcome-marketing.component').then(
      (m) => m.WelcomeMarketingComponent,
    ),
  'customer-service': () =>
    import(
      './pages/welcome/customer-service/welcome-customer-service.component'
    ).then((m) => m.WelcomeCustomerServiceComponent),
  'purchases-expenses': () =>
    import('./pages/welcome/welcome-module-placeholder.component').then(
      (m) => m.WelcomeModulePlaceholderComponent,
    ),
  suppliers: () =>
    import('./pages/welcome/welcome-module-placeholder.component').then(
      (m) => m.WelcomeModulePlaceholderComponent,
    ),
  sales: () =>
    import('./pages/welcome/welcome-module-placeholder.component').then(
      (m) => m.WelcomeModulePlaceholderComponent,
    ),
  clients: () =>
    import('./pages/welcome/welcome-module-placeholder.component').then(
      (m) => m.WelcomeModulePlaceholderComponent,
    ),
  titulation: () =>
    import('./pages/welcome/welcome-module-placeholder.component').then(
      (m) => m.WelcomeModulePlaceholderComponent,
    ),
  'human-resources': () =>
    import('./pages/welcome/welcome-module-placeholder.component').then(
      (m) => m.WelcomeModulePlaceholderComponent,
    ),
  'fixed-assets': () =>
    import('./pages/welcome/welcome-module-placeholder.component').then(
      (m) => m.WelcomeModulePlaceholderComponent,
    ),
  requisitions: () =>
    import('./pages/welcome/requisitions/welcome-requisitions.component').then(
      (m) => m.WelcomeRequisitionsComponent,
    ),
  'purchase-orders': () =>
    import(
      './pages/welcome/purchase-orders/welcome-purchase-orders.component'
    ).then((m) => m.WelcomePurchaseOrdersComponent),
  expenses: () =>
    import('./pages/welcome/expenses/welcome-expenses.component').then(
      (m) => m.WelcomeExpensesComponent,
    ),
  'service-requests': () =>
    import(
      './pages/welcome/service-requests/welcome-service-requests.component'
    ).then((m) => m.WelcomeServiceRequestsComponent),
  purchases: () =>
    import('./pages/welcome/purchases/welcome-purchases.component').then(
      (m) => m.WelcomePurchasesComponent,
    ),
} satisfies Record<string, NonNullable<Route['loadComponent']>>;

type WelcomeSectionPath = keyof typeof WELCOME_SECTION_LOADERS;

const MANAGEMENT_SECTIONS = [
  'executive',
  'operations',
  'inventory',
  'warehouses',
  'accounting',
  'taxes',
  'marketing',
  'customer-service',
] as const satisfies readonly WelcomeSectionPath[];

function siteResidentRequisitionRoute(view: 'purchases' | 'expenses'): Route {
  return {
    path: `site-resident/requisitions/${view}`,
    pathMatch: 'full',
    loadComponent: () =>
      import(
        './pages/welcome/requisitions/site-resident/welcome-site-resident-requisitions.component'
      ).then((m) => m.WelcomeSiteResidentRequisitionsComponent),
  };
}

function treasuryProfileRoutes(profile: string): Route[] {
  return [
    {
      path: `${profile}/treasury/weekly`,
      pathMatch: 'full',
      loadComponent: () =>
        import(
          './pages/welcome/treasury/weekly/welcome-treasury-weekly.component'
        ).then((m) => m.WelcomeTreasuryWeeklyComponent),
    },
    {
      path: `${profile}/treasury/monthly`,
      pathMatch: 'full',
      loadComponent: () =>
        import(
          './pages/welcome/treasury/monthly/welcome-treasury-monthly.component'
        ).then((m) => m.WelcomeTreasuryMonthlyComponent),
    },
    {
      path: `${profile}/treasury/yearly`,
      pathMatch: 'full',
      loadComponent: () =>
        import(
          './pages/welcome/treasury/yearly/welcome-treasury-yearly.component'
        ).then((m) => m.WelcomeTreasuryYearlyComponent),
    },
  ];
}

const PURCHASING_SECTIONS = [
  'requisitions',
  'purchase-orders',
  'purchases',
  'expenses',
  'warehouses',
] as const satisfies readonly WelcomeSectionPath[];

function executiveGeneralManagementRoutes(): Route[] {
  return EXECUTIVE_VIEWS.map((view) => ({
    path: `general-management/executive/${view.id}`,
    pathMatch: 'full',
    loadComponent:
      view.id === 'gerencial'
        ? () =>
            import(
              './pages/welcome/executive/gerencial/welcome-executive-gerencial.component'
            ).then((m) => m.WelcomeExecutiveGerencialComponent)
        : () =>
            import(
              './pages/welcome/executive/welcome-executive-placeholder.component'
            ).then((m) => m.WelcomeExecutivePlaceholderComponent),
    data: {
      title: view.label,
      layoutNotice:
        view.id === 'project-summary' || view.id === 'actual-vs-budget',
    },
  }));
}

function welcomeProfileRoutes(
  profile: string,
  sections: readonly WelcomeSectionPath[],
): Route[] {
  return sections.map((section) => ({
    path: `${profile}/${section}/general`,
    pathMatch: 'full',
    loadComponent: WELCOME_SECTION_LOADERS[section],
  }));
}

export const appRoutes: Route[] = [
  { path: '', pathMatch: 'full', redirectTo: 'login' },
  {
    path: 'login',
    title: 'Login',
    loadComponent: () =>
      import('./pages/login/login.component').then((m) => m.LoginComponent),
  },
  {
    path: 'welcome',
    title: 'Panel de control',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/welcome/welcome.component').then(
        (m) => m.WelcomeComponent,
      ),
    children: [
      ...welcomeProfileRoutes('general-management', [
        ...MANAGEMENT_SECTIONS.filter((section) => section !== 'executive'),
        'purchases-expenses',
        'suppliers',
        'sales',
        'clients',
        'titulation',
        'human-resources',
        'fixed-assets',
      ]),
      ...executiveGeneralManagementRoutes(),
      ...treasuryProfileRoutes('general-management'),
      ...welcomeProfileRoutes('operations-management', [
        ...MANAGEMENT_SECTIONS,
        'purchases-expenses',
        'suppliers',
        'sales',
        'clients',
        'titulation',
        'human-resources',
        'fixed-assets',
      ]),
      ...treasuryProfileRoutes('operations-management'),
      ...welcomeProfileRoutes(
        'purchasing-management',
        PURCHASING_SECTIONS,
      ),
      ...welcomeProfileRoutes(
        'purchasing-assistant',
        PURCHASING_SECTIONS,
      ),
      {
        path: 'site-resident/requisitions/general',
        pathMatch: 'full',
        redirectTo: '/welcome/site-resident/requisitions/purchases',
      },
      siteResidentRequisitionRoute('purchases'),
      siteResidentRequisitionRoute('expenses'),
      ...welcomeProfileRoutes('site-resident', [
        'purchase-orders',
        'expenses',
        'warehouses',
        'service-requests',
      ]),
      ...welcomeProfileRoutes('warehouse-management', ['warehouses']),
    ],
  },
];
